import React from 'react';
import { Card, Row, Col, Tag, Typography, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';
import type { OrderData } from '@/shared/api/types';
import { formatDate, formatCurrency, getStatusColor, getStatusText, getShippingStatusColor, getShippingStatusText } from '../../query/utils/orderUtils';

const { Title, Text } = Typography;

export interface OrderInfoCardProps {
  /** 订单数据 */
  orderData: OrderData;
  /** 是否显示完整信息 */
  showFullInfo?: boolean;
  /** 是否可编辑 */
  editable?: boolean;
}

const OrderInfoCard: React.FC<OrderInfoCardProps> = ({
  orderData,
  showFullInfo = true,
  editable = false
}) => {
  const { t } = useTranslation(['modules', 'common']);

  // 订单基本信息
  const orderInfo = [
    {
      label: t('modules:order.detail.info.orderId', '订单ID'),
      value: orderData.order_id,
      span: 2
    },
    {
      label: t('modules:order.detail.info.status', '状态'),
      value: (
        <Tag color={getStatusColor(orderData.status)}>
          {getStatusText(orderData.status)}
        </Tag>
      ),
      span: 1
    },
    {
      label: t('modules:order.detail.info.orderDate', '下单时间'),
      value: formatDate(orderData.ordered_at, 'long'),
      span: 2
    },
    {
      label: t('modules:order.detail.info.totalAmount', '订单总额'),
      value: (
        <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
          {formatCurrency(orderData.total_amount, orderData.currency)}
        </Text>
      ),
      span: 1
    },
    {
      label: t('modules:order.detail.info.customerId', '客户ID'),
      value: orderData.customer_id,
      span: 1
    },
    {
      label: t('modules:order.detail.info.platform', '订单平台'),
      value: orderData.platform_source,
      span: 1
    },
    {
      label: t('modules:order.detail.info.currency', '货币'),
      value: orderData.currency,
      span: 1
    }
  ];

  // 客户信息
  const customerInfo = orderData.customer_info ? [
    {
      label: t('modules:order.detail.info.customerName', '客户姓名'),
      value: orderData.customer_info.name || '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.customerEmail', '客户邮箱'),
      value: orderData.customer_info.email || '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.customerPhone', '客户电话'),
      value: orderData.customer_info.phone || '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.customerAddress', '客户地址'),
      value: orderData.customer_info.address || '-',
      span: 3
    }
  ] : [];

  // 配送信息
  const deliveryInfo = [
    {
      label: t('modules:order.detail.info.estimatedDelivery', '预计送达'),
      value: orderData.estimated_delivery_date
        ? formatDate(orderData.estimated_delivery_date, 'short')
        : '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.shippingAddress', '配送地址'),
      value: orderData.shipping_address || '-',
      span: 3
    },
    // 新增物流字段
    {
      label: t('modules:order.detail.info.shippingMethod', '配送方式'),
      value: orderData.shipping_method || '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.shippingCarrier', '物流承运商'),
      value: orderData.shipping_carrier || '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.shippingCost', '运费'),
      value: orderData.shipping_cost !== undefined && orderData.shipping_cost !== null
        ? formatCurrency(orderData.shipping_cost, orderData.currency)
        : '包邮',
      span: 2
    },
    {
      label: t('modules:order.detail.info.shippingStatus', '物流状态'),
      value: (
        <Tag color={getShippingStatusColor(orderData.shipping_status || 'pending')}>
          {getShippingStatusText(orderData.shipping_status || 'pending', t)}
        </Tag>
      ),
      span: 2
    },
    {
      label: t('modules:order.detail.info.trackingNumber', '物流追踪号'),
      value: orderData.tracking_number
        ? (
            <a 
              href={orderData.tracking_url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1890ff' }}
            >
              {orderData.tracking_number}
            </a>
          )
        : '-',
      span: 3
    },
    {
      label: t('modules:order.detail.info.shippedAt', '发货时间'),
      value: orderData.shipped_at
        ? formatDate(orderData.shipped_at, 'long')
        : '-',
      span: 2
    },
    {
      label: t('modules:order.detail.info.deliveredAt', '送达时间'),
      value: orderData.delivered_at
        ? formatDate(orderData.delivered_at, 'long')
        : '-',
      span: 2
    }
  ];



  return (
    <Card className="order-info-card">
      <Title level={5} style={{ marginBottom: 16 }}>
        {t('modules:order.detail.info.title', '订单信息')}
      </Title>

      {/* 订单基本信息 */}
      <Descriptions
        title={t('modules:order.detail.info.basicTitle', '基本信息')}
        bordered
        column={{ xs: 1, sm: 2, md: 3 }}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {orderInfo.map((info, index) => (
          <Descriptions.Item key={index} label={info.label} span={info.span}>
            {info.value}
          </Descriptions.Item>
        ))}
      </Descriptions>

      {/* 客户信息 */}
      {showFullInfo && customerInfo.length > 0 && (
        <Descriptions
          title={t('modules:order.detail.info.customerTitle', '客户信息')}
          bordered
          column={{ xs: 1, sm: 2, md: 3 }}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {customerInfo.map((info, index) => (
            <Descriptions.Item key={index} label={info.label} span={info.span}>
              {info.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {/* 配送信息 */}
      {showFullInfo && deliveryInfo.length > 0 && (
        <Descriptions
          title={t('modules:order.detail.info.deliveryTitle', '配送信息')}
          bordered
          column={{ xs: 1, sm: 2, md: 3 }}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {deliveryInfo.map((info, index) => (
            <Descriptions.Item key={index} label={info.label} span={info.span}>
              {info.value}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}




    </Card>
  );
};

export default OrderInfoCard;